import {getAnyValue} from "../../util/misc";
import {IItemCheckbox} from "./td-checkbox";
import {IDropdownParams} from "./th-checkbox";

export namespace nsSearchToggle {

    interface ICheckboxBase<T> {
        key: keyof T | undefined
        selected: KnockoutObservableArray<T>
    }

    export interface IAllCheckbox<T> extends ICheckboxBase<T> {
        data: KnockoutObservableArray<T> | T[] | KnockoutComputed<T[]> | (() => T[])
        fnIsSelectable?: ((item: T) => boolean) | undefined
        onToggleClick?: ((newState: boolean, data: this['data']) => void) | undefined
        dropdownParams?: IDropdownParams | undefined
    }

    interface I_ItemCheckbox<T> extends ICheckboxBase<T> {
        item: T
    }

    abstract class CheckboxBase<T>{
        public readonly key: keyof T | undefined
        public readonly selected: KnockoutObservableArray<T>

        constructor(protected params: ICheckboxBase<T>) {
            this.selected = params.selected
            this.key = params.key
        }

        abstract toggle(): void
        abstract isChecked(): boolean

        protected getSelectedItem(item: T): T | undefined {
            if (this.key) {
                const key = this.key;
                return this.selected().find(o => o[key] == item[key])
            }
            return this.selected().find(o => o == item)
        }
    }

    export class ItemCheckbox<T> extends CheckboxBase<T> implements IItemCheckbox {
        private readonly item: T

        constructor(protected override params: I_ItemCheckbox<T>) {
            super(params)
            this.item = params.item
        }

        toggle(): void {
            if (!this.isEnabled()) {
                return;
            }
            const bool = this.isChecked()
            if (bool) {
            const selectedItem = this.getSelectedItem(this.item);
            if (selectedItem != null) {
                this.selected.remove(selectedItem);
            }
            } else {
                this.selected.push(this.item)
            }
        }

        isChecked(): boolean {
            const item = this.getSelectedItem(this.item);
            return item != null && this.selected().includes(item)
        }

        isEnabled(): boolean {
            return true
        }

        getTitle(): string | number | null {
            return null
        }

        show(): boolean {
            return true
        }
    }

    export class AllCheckbox<T> extends CheckboxBase<T> {
        public readonly data: KnockoutObservableArray<T> | T[] | KnockoutComputed<T[]> | (() => T[])
        public dropdownParams?: IDropdownParams | undefined
        private readonly fnIsSelectable: (item: T) => boolean

        constructor(protected override params: IAllCheckbox<T>) {
            super(params)
            this.data = params.data
            if (params.dropdownParams !== undefined) {
                this.dropdownParams = params.dropdownParams;
            }
            this.fnIsSelectable = params.fnIsSelectable ?? (() => true)
        }

        /** Items on the current page that the header checkbox may toggle. */
        private getSelectablePageItems(): T[] {
            return getAnyValue(this.data).filter(o => this.fnIsSelectable(o))
        }

        isEnabled(): boolean {
            return this.getSelectablePageItems().length > 0
        }

        toggle(): void {
            if (!this.isEnabled()) {
                return
            }
            const selectable = this.getSelectablePageItems()
            const arr = this.selected()
            const bool = this.isChecked()
            this.params.onToggleClick?.(!bool, this.data)
            if (bool) {
                // remove
                selectable.forEach((o: T) => {
                    const selectedItem = this.getSelectedItem(o);
                    if (selectedItem) {
                        const index = arr.indexOf(selectedItem)
                        arr.splice(index, 1);
                    }
                })
            } else {
                // add
                selectable.forEach((o: T) => {
                    if (!this.getSelectedItem(o)) {
                        arr.push(o)
                    }
                })
            }
            this.selected(arr)
        }

        isIndeterminate(): boolean {
            if (this.isChecked()) {
                return false
            }
            const selectable = this.getSelectablePageItems()
            if (!selectable.length) {
                return false
            }
            const selectedCount = selectable.filter((o: T) => !!this.getSelectedItem(o)).length
            return selectedCount > 0 && selectedCount < selectable.length
        }

        isChecked(): boolean {
            const selectable = this.getSelectablePageItems()
            if (!selectable.length) {
                return false
            }
            if (this.key) {
                const key = this.key;
                return selectable.every((o: T) => !!this.selected().find(s => o[key] == s[key]))
            }
            return selectable.every((o: T) => this.selected().includes(o))
        }

    }

}